
import Vue from "vue";
import { TooltipStep, TooltipPosition } from "@/interfaces/shared/tooltip";

export default Vue.extend({
  name: "GuidedTooltip",
  props: {
    steps: {
      type: Array as () => TooltipStep[],
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    defaultPosition: {
      type: Object as () => TooltipPosition,
      default: () => ({
        horizontal: "center",
        vertical: "top"
      })
    },
    dimBackground: {
      type: Boolean,
      default: true
    },
    highlightTarget: {
      type: Boolean,
      default: false
    },
    showBackdrop: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    // Text customization
    skipText: {
      type: String,
      default: "Skip"
    },
    nextText: {
      type: String,
      default: "Next"
    },
    previousText: {
      type: String,
      default: "Previous"
    },
    finishText: {
      type: String,
      default: "Finish"
    }
  },
  data() {
    return {
      currentStepIndex: 0,
      tooltipStyle: {}
    };
  },
  computed: {
    currentStepData(): TooltipStep | undefined {
      return this.steps[this.currentStepIndex];
    },
    totalSteps(): number {
      return this.steps.length;
    },
    hasArrow(): boolean {
      return Boolean(this.shouldHighlightTarget && this.currentStepData?.target);
    },
    shouldHighlightTarget(): boolean {
      // Use step-specific highlighting if defined, otherwise fall back to component prop
      if (this.currentStepData?.highlightTarget !== undefined) {
        return this.currentStepData.highlightTarget;
      }
      return this.highlightTarget;
    },
    showSkipButton(): boolean {
      return this.currentStepIndex < this.totalSteps - 1;
    },
    showNextButton(): boolean {
      return this.currentStepIndex < this.totalSteps - 1;
    },
    showPreviousButton(): boolean {
      return this.currentStepIndex > 0;
    },
    showFinishButton(): boolean {
      return this.currentStepIndex === this.totalSteps - 1;
    },
    positionClasses(): string[] {
      const classes = [];
      const position = this.currentStepData?.position || this.defaultPosition;
      
      classes.push(`tooltip-${position.horizontal}`);
      classes.push(`tooltip-${position.vertical}`);
      
      return classes;
    },
    arrowClasses(): string[] {
      const classes = [];
      const position = this.currentStepData?.position || this.defaultPosition;
      
      classes.push(`arrow-${position.vertical}`);
      
      if (position.horizontal === "center") {
        classes.push("arrow-center");
      } else if (position.horizontal === "left") {
        classes.push("arrow-left");
      } else {
        classes.push("arrow-right");
      }
      
      return classes;
    },
    tooltipPosition(): { [key: string]: string } {
      const position = this.currentStepData?.position || this.defaultPosition;
      const targetOffset = this.currentStepData?.targetOffset || {};
      const style: { [key: string]: string } = {};
      
      if (this.currentStepData?.target) {
        const targetElement = document.querySelector(this.currentStepData.target);
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect();
          
          // Calculate vertical position with offset
          let topPosition = 0;
          if (position.vertical === "top") {
            topPosition = rect.top - 10 + (targetOffset.top || 0);
          } else {
            topPosition = rect.bottom + 10 + (targetOffset.top || 0);
          }
          style.top = `${topPosition}px`;
          
          // Calculate horizontal position with offset
          if (position.horizontal === "left") {
            const leftPosition = rect.left + (targetOffset.left || 0);
            style.left = `${leftPosition}px`;
          } else if (position.horizontal === "center") {
            const centerPosition = rect.left + rect.width / 2 + (targetOffset.left || 0);
            style.left = `${centerPosition}px`;
            style.transform = "translateX(-50%)";
          } else {
            const rightPosition = window.innerWidth - rect.right + (targetOffset.right || 0);
            style.right = `${rightPosition}px`;
          }
        } else {
          // Fallback to center positioning
          style.left = "50%";
          style.top = "50%";
          style.transform = "translate(-50%, -50%)";
        }
      } else {
        // Center positioning when no target
        style.left = "50%";
        style.top = "50%";
        style.transform = "translate(-50%, -50%)";
      }
      
      return style;
    },
    arrowPosition(): { [key: string]: string } {
      const position = this.currentStepData?.position || this.defaultPosition;
      const style: { [key: string]: string } = {};
      
      // Calculate arrow position based on target element
      if (this.currentStepData?.target && this.shouldHighlightTarget) {
        const targetElement = document.querySelector(this.currentStepData.target);
        if (targetElement) {
          const tooltipElement = document.querySelector('.guided-tooltip');
          const targetRect = targetElement.getBoundingClientRect();
          
          if (tooltipElement) {
            const tooltipRect = tooltipElement.getBoundingClientRect();
            
            // Calculate where arrow should point on target element
            const targetCenterX = targetRect.left + targetRect.width / 2;
            const tooltipCenterX = tooltipRect.left + tooltipRect.width / 2;
            
            // Position arrow to point at target
            const arrowOffset = Math.max(0, Math.min(40, targetCenterX - tooltipRect.left - 15));
            style.left = `${arrowOffset}px`;
          } else {
            // Fallback positioning
            if (position.horizontal === "left") {
              style.left = "20px";
            } else {
              style.right = "20px";
            }
          }
        }
      } else {
        // Default positioning when no specific target
        if (position.horizontal === "left") {
          style.left = "20px";
        } else if (position.horizontal === "center") {
          style.left = "50%";
          style.transform = "translateX(-50%)";
        } else {
          style.right = "20px";
        }
      }
      
      return style;
    },
    highlightPosition(): { [key: string]: string } {
      if (!this.currentStepData?.target) return {};
      
      const targetElement = document.querySelector(this.currentStepData.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        return {
          position: "fixed",
          top: `${rect.top - 4}px`,
          left: `${rect.left - 4}px`,
          width: `${rect.width + 8}px`,
          height: `${rect.height + 8}px`
        };
      }
      
      return {};
    },
    targetHighlightStyle(): { [key: string]: string } {
      return this.highlightPosition;
    }
  },
  watch: {
    visible(newVisible) {
      if (newVisible && this.steps.length > 0) {
        this.updatePosition();
        this.$nextTick(() => {
          this.$emit("tooltip-shown");
        });
      }
    },
    currentStepData: {
      handler() {
        this.$nextTick(() => {
          this.updatePosition();
        });
      },
      deep: true
    }
  },
  methods: {
    updatePosition() {
      // Trigger re-calculation of position
      this.$forceUpdate();
    },
    nextStep() {
      if (this.currentStepIndex < this.totalSteps - 1) {
        this.currentStepIndex++;
        this.$emit("step-changed", this.currentStepIndex);
      }
    },
    previousStep() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
        this.$emit("step-changed", this.currentStepIndex);
      }
    },
    skipTooltip() {
      this.$emit("tooltip-skipped");
      this.closeTooltip();
    },
    finishTooltip() {
      this.$emit("tooltip-finished");
      this.closeTooltip();
    },
    closeTooltip() {
      this.$emit("tooltip-closed");
    }
    // Removed handleBackdropClick to prevent closing on backdrop click
  }
});
